import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Phone, Instagram, MapPin, CheckCircle2, Calendar, MessageCircle, Minus, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';

export default function Contact() {
  const location = useLocation();
  const [formStatus, setFormStatus] = useState('idle');
  const [subject, setSubject] = useState('Generel henvendelse');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sub = params.get('subject');
    if (sub === 'book-visit') {
      setSubject('Book et besøg');
    } else if (sub === 'order-samples') {
      setSubject('Bestil prøver');
    }
  }, [location]);

  const contactSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Kontakt Cards by Eve",
    "description": "Kontakt Cards by Eve i dag via e-mail, telefon eller vores kontaktformular for spørgsmål, forhandlerforespørgsler eller mødebooking.",
    "mainEntity": {
      "@type": "LocalBusiness",
      "name": "Cards by Eve",
      "email": "evelyn@cardsbyeve.dk",
      "telephone": "+4531356040",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Højslev",
        "postalCode": "7840",
        "addressCountry": "DK"
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('submitting');
    
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());
    
    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      
      if (response.ok) {
        setFormStatus('success');
        e.target.reset();
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      setFormStatus('error');
    }
  };

  return (
    <div className="bg-white min-h-screen">
      <SEO 
        title="Kontakt mig - Lad os skabe forbindelse"
        description="Har du spørgsmål til min kollektion, eller ønsker du at blive forhandler? Kontakt Cards by Eve i dag via e-mail, telefon eller kontaktformularen."
        schema={contactSchema}
      />
      {/* Header */}
      <header className="pt-32 pb-20 bg-brand-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-4 mb-8">
            <Minus className="w-6 h-6 text-brand-950" />
            <span className="text-brand-400 text-xs uppercase tracking-[0.3em] font-medium">
              Kontakt
            </span>
          </div>
          <h1 className="text-6xl md:text-8xl font-serif text-brand-950 leading-tight">
            Lad os skabe <br />
            <span className="italic">forbindelse</span>
          </h1>
        </div>
      </header>

      <section className="py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-24">
            
            {/* Contact Info */}
            <div className="lg:col-span-5 space-y-16">
              <div className="space-y-8">
                <h2 className="text-4xl font-serif text-brand-950">Jeg glæder mig til <br />at høre fra dig</h2>
                <p className="text-brand-600 text-lg font-light leading-relaxed">
                  Har du spørgsmål til min kollektion, eller ønsker du at blive forhandler? Jeg er altid åben for nye samtaler og samarbejder.
                </p>
              </div>

              <div className="space-y-10">
                <div className="flex items-start space-x-6">
                  <div className="w-12 h-12 bg-brand-950 flex items-center justify-center text-white shrink-0">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-xs uppercase tracking-widest font-bold text-brand-950 mb-2">E-mail</h3>
                    <a href="mailto:evelyn@cardsbyeve.dk" className="text-brand-600 hover:text-brand-950 transition-colors font-light">
                      evelyn@cardsbyeve.dk
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-6">
                  <div className="w-12 h-12 bg-brand-950 flex items-center justify-center text-white shrink-0">
                    <Phone className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-xs uppercase tracking-widest font-bold text-brand-950 mb-2">Telefon</h3>
                    <a href="tel:+4531356040" className="text-brand-600 hover:text-brand-950 transition-colors font-light">
                      31 35 60 40
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-6">
                  <div className="w-12 h-12 bg-brand-950 flex items-center justify-center text-white shrink-0">
                    <Instagram className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-xs uppercase tracking-widest font-bold text-brand-950 mb-2">Instagram</h3>
                    <a href="https://www.instagram.com/cards_by_eve" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:text-brand-950 transition-colors font-light">
                      @cards_by_eve
                    </a>
                  </div>
                </div>
              </div>

              <div className="p-10 bg-brand-50 rounded-sm border border-brand-100">
                <div className="flex items-center space-x-3 mb-6">
                  <Calendar className="w-5 h-5 text-brand-950" />
                  <h3 className="text-xl font-serif text-brand-950">Book et besøg</h3>
                </div>
                <p className="text-brand-600 font-light mb-8 leading-relaxed">
                  Er du forhandler? Jeg kommer gerne forbi din butik med min fulde kollektion, så du kan se og mærke kvaliteten.
                </p>
                <button 
                  onClick={() => setSubject('Book et besøg')}
                  className="text-brand-950 text-xs uppercase tracking-widest font-bold flex items-center group"
                >
                  Vælg denne mulighed i formularen <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-7">
              <div className="bg-white p-8 md:p-16 border border-brand-100 shadow-2xl relative">
                {formStatus === 'success' ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="h-full flex flex-col items-center justify-center text-center py-20"
                  >
                    <CheckCircle2 className="w-16 h-16 text-brand-950 mb-8" />
                    <h3 className="text-4xl font-serif text-brand-950 mb-6">Beskeden er sendt</h3>
                    <p className="text-brand-600 font-light mb-12 max-w-xs mx-auto">
                      Tak for din henvendelse. Jeg vender tilbage til dig så hurtigt som muligt.
                    </p>
                    <button
                      onClick={() => setFormStatus('idle')}
                      className="border border-brand-950 text-brand-950 px-10 py-4 text-xs uppercase tracking-widest font-bold hover:bg-brand-950 hover:text-white transition-all"
                    >
                      Send en ny besked
                    </button>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-10">
                    <div className="flex items-center space-x-3 mb-12">
                      <MessageCircle className="w-4 h-4 text-brand-950" />
                      <span className="text-xs font-bold text-brand-950 uppercase tracking-[0.2em]">Send en besked</span>
                    </div>

                    {/* Honeypot */}
                    <div className="sr-only absolute" aria-hidden="true">
                      <input type="text" name="website_url" tabIndex="-1" autoComplete="off" />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                      <div className="space-y-2">
                        <label htmlFor="name" className="text-[10px] uppercase tracking-widest font-bold text-brand-400">Navn</label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          className="w-full bg-transparent border-b border-brand-200 py-3 focus:border-brand-950 outline-none transition-colors font-light text-brand-950"
                          placeholder="Dit fulde navn"
                        />
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="email" className="text-[10px] uppercase tracking-widest font-bold text-brand-400">E-mail</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          className="w-full bg-transparent border-b border-brand-200 py-3 focus:border-brand-950 outline-none transition-colors font-light text-brand-950"
                          placeholder="din@email.dk"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="subject" className="text-[10px] uppercase tracking-widest font-bold text-brand-400">Emne</label>
                      <select
                        id="subject"
                        name="subject"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        className="w-full bg-transparent border-b border-brand-200 py-3 focus:border-brand-950 outline-none transition-colors font-light text-brand-950 appearance-none cursor-pointer"
                      >
                        <option value="Generel henvendelse">Generel henvendelse</option>
                        <option value="Book et besøg">Book et besøg (Forhandler)</option>
                        <option value="Bestil prøver">Bestil prøver</option>
                        <option value="Spørgsmål til produkter">Spørgsmål til produkter</option>
                        <option value="Andet">Andet</option>
                      </select>
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="message" className="text-[10px] uppercase tracking-widest font-bold text-brand-400">Besked</label>
                      <textarea
                        id="message"
                        name="message"
                        rows="4"
                        required
                        className="w-full bg-transparent border-b border-brand-200 py-3 focus:border-brand-950 outline-none transition-colors font-light text-brand-950 resize-none"
                        placeholder="Hvordan kan jeg hjælpe dig?"
                      ></textarea>
                    </div>

                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="w-full bg-brand-950 text-white py-6 text-xs uppercase tracking-widest font-bold hover:bg-brand-800 transition-all disabled:opacity-50 flex items-center justify-center group"
                    >
                      {formStatus === 'submitting' ? (
                        <span className="flex items-center">
                          <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Sender...
                        </span>
                      ) : (
                        <span className="flex items-center">
                          Send besked <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </span>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
